import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Calculator, TrendingUp, AlertTriangle, CheckCircle2, XCircle, ExternalLink, Shield, DollarSign, MapPin, ArrowRight } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const EQUIPMENT_OPTIONS = [
  { code: "dry_van", label: "Dry Van" },
  { code: "reefer", label: "Reefer" },
  { code: "flatbed", label: "Flatbed" },
  { code: "step_deck", label: "Step Deck" },
  { code: "power_only", label: "Power Only" },
  { code: "hotshot", label: "Hotshot" },
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA",
  "ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK",
  "OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

/**
 * RateCheckPage — free fair-lane-rate calculator with verification verdict.
 *
 * Route: /rate-check
 *
 * Uses POST /api/rate-check backend endpoint. Takes origin, destination,
 * equipment type, and optionally the broker's offered rate. Returns a fair
 * range with a verification verdict (AVOID / LOWBALL / FAIR / GENEROUS / TOO_GOOD).
 */
export default function RateCheckPage() {
  const [originCity, setOriginCity] = useState("");
  const [originState, setOriginState] = useState("");
  const [destCity, setDestCity] = useState("");
  const [destState, setDestState] = useState("");
  const [equipment, setEquipment] = useState("dry_van");
  const [offeredRate, setOfferedRate] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const runCheck = async (e) => {
    e?.preventDefault();
    if (!originCity || !originState || !destCity || !destState) {
      setError("Fill in both origin and destination cities/states.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);

    const payload = {
      origin_city: originCity.trim(),
      origin_state: originState.trim().toUpperCase(),
      dest_city: destCity.trim(),
      dest_state: destState.trim().toUpperCase(),
      equipment,
    };
    if (offeredRate.trim() && !isNaN(parseFloat(offeredRate))) {
      payload.offered_rate = parseFloat(offeredRate);
    }

    try {
      const { data } = await axios.post(`${API_BASE}/api/rate-check`, payload);
      setResult(data);
    } catch (err) {
      if (err.response?.status === 429) {
        setError("Too many checks in a short time — try again in an hour.");
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Something went wrong. Try again with a nearby major city.");
      }
    } finally {
      setLoading(false);
    }
  };

  const useExample = () => {
    setOriginCity("Sioux Falls");
    setOriginState("SD");
    setDestCity("Chicago");
    setDestState("IL");
    setEquipment("dry_van");
    setOfferedRate("800");
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Nav */}
      <div className="border-b border-white/10 bg-neutral-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="rate-check-home-link">
            <span className="font-black text-xl tracking-tight">Freight</span>
            <span className="font-black text-xl tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-xl tracking-tight">AI</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/broker-check"
              className="text-sm text-white/70 hover:text-white transition-colors hidden sm:inline"
              data-testid="rate-check-brokercheck-link"
            >
              BrokerCheck →
            </Link>
            <Link
              to="/"
              className="text-sm text-white/70 hover:text-white transition-colors"
              data-testid="rate-check-back-link"
            >
              ← Home
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 30%, #FF5A1F 0%, transparent 40%), radial-gradient(circle at 70% 70%, #FF5A1F 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 pb-12 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-1.5 text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-6">
            <Calculator className="w-3.5 h-3.5" />
            Free · No signup · Rate verification tool
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight mb-5">
            Never take a
            <br />
            <span className="text-[#FF5A1F]">lowball rate</span> again.
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
            Type in your lane. Get the fair-market rate range instantly. If a broker offered you a number, we tell you if it's a lowball, fair, or too-good-to-be-true. Based on 2026 market medians. Free forever.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-4xl mx-auto px-6 pb-8">
        <form onSubmit={runCheck} className="rounded-2xl border-2 border-white/10 bg-white/5 p-6 sm:p-8" data-testid="rate-check-form">
          <div className="grid sm:grid-cols-2 gap-6">
            {/* Origin */}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                From
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={originCity}
                  onChange={(e) => setOriginCity(e.target.value)}
                  placeholder="Sioux Falls"
                  className="flex-1 bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white placeholder-white/30 outline-none transition-colors"
                  data-testid="rate-check-origin-city"
                />
                <select
                  value={originState}
                  onChange={(e) => setOriginState(e.target.value)}
                  className="w-20 bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-2 py-2.5 text-white outline-none transition-colors"
                  data-testid="rate-check-origin-state"
                >
                  <option value="">ST</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Destination */}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                To
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={destCity}
                  onChange={(e) => setDestCity(e.target.value)}
                  placeholder="Chicago"
                  className="flex-1 bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white placeholder-white/30 outline-none transition-colors"
                  data-testid="rate-check-dest-city"
                />
                <select
                  value={destState}
                  onChange={(e) => setDestState(e.target.value)}
                  className="w-20 bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-2 py-2.5 text-white outline-none transition-colors"
                  data-testid="rate-check-dest-state"
                >
                  <option value="">ST</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Equipment */}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2">
                Equipment
              </div>
              <select
                value={equipment}
                onChange={(e) => setEquipment(e.target.value)}
                className="w-full bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white outline-none transition-colors"
                data-testid="rate-check-equipment"
              >
                {EQUIPMENT_OPTIONS.map((eq) => (
                  <option key={eq.code} value={eq.code}>{eq.label}</option>
                ))}
              </select>
            </div>

            {/* Offered rate (optional) */}
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2 flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5" />
                Broker's offered rate <span className="normal-case text-white/40 font-normal">(optional)</span>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                <input
                  type="number"
                  value={offeredRate}
                  onChange={(e) => setOfferedRate(e.target.value)}
                  placeholder="800"
                  min="0"
                  step="1"
                  className="w-full bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 pl-7 py-2.5 text-white placeholder-white/30 outline-none transition-colors"
                  data-testid="rate-check-offered"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#FF5A1F] hover:bg-[#FF7038] disabled:bg-white/10 disabled:text-white/40 text-black font-bold rounded-lg px-6 py-3 text-base transition-colors flex items-center justify-center gap-2"
              data-testid="rate-check-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Checking rates...
                </>
              ) : (
                <>
                  <TrendingUp className="w-5 h-5" />
                  Check the rate
                </>
              )}
            </button>
            <button
              type="button"
              onClick={useExample}
              className="text-sm text-white/60 hover:text-white underline transition-colors"
              data-testid="rate-check-example-btn"
            >
              Try example: Sioux Falls → Chicago ($800 offer)
            </button>
          </div>
        </form>
      </section>

      {/* Results */}
      {(error || result) && (
        <section className="max-w-4xl mx-auto px-6 pb-16">
          {error && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-6 text-center" data-testid="rate-check-error">
              <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {result && (
            <div className="space-y-5" data-testid="rate-check-result">
              {/* Verdict card (if offered rate given) */}
              {result.verdict && <VerdictCard v={result.verdict} totalMedian={result.total_range.median} />}

              {/* Distance + rate breakdown */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-6">
                <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-4">
                  Fair market range · {result.equipment.label}
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 text-sm">
                  <div className="flex-1">
                    <div className="text-white/50 text-[10px] uppercase tracking-widest mb-0.5">From</div>
                    <div className="text-white font-semibold">{result.origin.city}, {result.origin.state}</div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/40 hidden sm:block" />
                  <div className="flex-1">
                    <div className="text-white/50 text-[10px] uppercase tracking-widest mb-0.5">To</div>
                    <div className="text-white font-semibold">{result.destination.city}, {result.destination.state}</div>
                  </div>
                  <div className="flex-1 sm:text-right">
                    <div className="text-white/50 text-[10px] uppercase tracking-widest mb-0.5">Distance</div>
                    <div className="text-white font-semibold">{result.distance_miles.toLocaleString()} mi</div>
                  </div>
                </div>

                {/* Rate range visual */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <RateCard label="Low" cpm={result.cpm_range.low} total={result.total_range.low} tone="red" />
                  <RateCard label="Median" cpm={result.cpm_range.median} total={result.total_range.median} tone="green" />
                  <RateCard label="High" cpm={result.cpm_range.high} total={result.total_range.high} tone="blue" />
                </div>

                <div className="text-[11px] text-white/40 leading-snug pt-3 border-t border-white/10">
                  {result.disclaimer}
                </div>
              </div>

              {/* CTA to try another */}
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => { setResult(null); setError(""); }}
                  className="text-sm text-white/60 hover:text-white underline"
                  data-testid="rate-check-reset"
                >
                  Check another lane
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Educational */}
      <section className="border-t border-white/10 bg-neutral-900/50">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3 text-center">
            Why we built this
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-4 tracking-tight">
            Unverified brokers count on you not knowing the market rate. Now you do.
          </h2>
          <p className="text-white/70 text-center max-w-3xl mx-auto mb-12 leading-relaxed">
            The #1 way unverified brokers pressure small carriers is lowballing the rate — then either ghosting after delivery, disputing the invoice, or paying 70 days later. If you know the market rate BEFORE you accept, they can't run this play on you.
          </p>

          <div className="grid md:grid-cols-3 gap-5">
            <BenefitCard
              icon={<TrendingUp className="w-6 h-6" />}
              title="Real 2026 market rates"
              body="Baselines built from DAT, Truckstop, and GreenScreens Q2 2026 median rates. Adjusted for regional lane supply/demand."
            />
            <BenefitCard
              icon={<AlertTriangle className="w-6 h-6" />}
              title="Instant rate verdict"
              body="Enter what the broker offered — we tell you if it's a lowball, fair, or a red-flag signal in seconds."
            />
            <BenefitCard
              icon={<CheckCircle2 className="w-6 h-6" />}
              title="Free, always"
              body="No signup. No email required. No 'unlock full rate' upsell. Just the number, no strings."
            />
          </div>

          {/* Pair with BrokerCheck */}
          <div className="mt-14 rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
            <div className="w-14 h-14 rounded-xl bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center shrink-0">
              <Shield className="w-7 h-7" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1">
                Pair this with BrokerCheck
              </div>
              <div className="text-lg font-bold mb-1">Verify the broker before you check the rate.</div>
              <p className="text-white/60 text-sm">
                RateCheck tells you if the price is fair. BrokerCheck tells you if the broker is real. Use both, always.
              </p>
            </div>
            <Link
              to="/broker-check"
              className="bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg px-5 py-2.5 transition-colors flex items-center gap-2 shrink-0"
              data-testid="rate-check-brokercheck-cta"
            >
              Open BrokerCheck
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>

          {/* Bottom CTA */}
          <div className="mt-8 rounded-2xl border-2 border-[#FF5A1F]/40 bg-gradient-to-br from-[#FF5A1F]/20 to-transparent p-8 text-center">
            <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3">
              Want the full toolkit?
            </div>
            <h3 className="text-2xl sm:text-3xl font-black mb-3 tracking-tight">
              FreightPilotAI is the complete operating system for small carriers.
            </h3>
            <p className="text-white/70 max-w-xl mx-auto mb-6">
              RateCheck + BrokerCheck + DNP + Careers Builder are 4 tools of 6 we're building. Chrome load-board filter and docs vault coming next. All for carriers under 20 trucks.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-6 py-3 transition-colors"
              data-testid="rate-check-cta-signup"
            >
              Set It Up For Me — Free
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-white/50">
          <div>
            © 2026 <b className="text-white">FreightPilotAI</b> · Distance via OSM · Rate baselines Q2 2026
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/broker-check" className="hover:text-white">BrokerCheck</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ─────────────────── Verdict card ─────────────────── */
function VerdictCard({ v, totalMedian }) {
  const styles = {
    AVOID:    { border: "border-red-500/60",    bg: "bg-red-500/15",    text: "text-red-100",    icon: <XCircle className="w-14 h-14 text-red-400" /> },
    LOWBALL:  { border: "border-amber-500/60",  bg: "bg-amber-500/15",  text: "text-amber-100",  icon: <AlertTriangle className="w-14 h-14 text-amber-400" /> },
    FAIR:     { border: "border-emerald-500/60",bg: "bg-emerald-500/15",text: "text-emerald-100",icon: <CheckCircle2 className="w-14 h-14 text-emerald-400" /> },
    GENEROUS: { border: "border-blue-500/60",   bg: "bg-blue-500/15",   text: "text-blue-100",   icon: <TrendingUp className="w-14 h-14 text-blue-400" /> },
    TOO_GOOD: { border: "border-red-500/60",    bg: "bg-red-500/15",    text: "text-red-100",    icon: <AlertTriangle className="w-14 h-14 text-red-400" /> },
  };
  const s = styles[v.code] || styles.FAIR;

  return (
    <div className={`rounded-2xl border-2 ${s.border} ${s.bg} p-6 sm:p-8`}>
      <div className="flex items-start gap-5">
        <div className="shrink-0">{s.icon}</div>
        <div className="flex-1">
          <div className={`text-[11px] uppercase tracking-widest font-bold mb-1 ${s.text} opacity-70`}>
            Verdict on their ${v.offered_rate.toLocaleString()} offer
          </div>
          <h2 className={`text-2xl sm:text-3xl font-black mb-2 ${s.text} tracking-tight`}>
            {v.label}
          </h2>
          <div className={`flex items-center gap-4 text-sm mb-3 ${s.text} opacity-90`}>
            <span>
              <b>{v.pct_of_median}%</b> of fair-market median
            </span>
            <span>·</span>
            <span>
              Offered ${v.offered_cpm}/mi
            </span>
          </div>
          <p className={`${s.text} opacity-90 leading-snug`}>{v.message}</p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Rate card ─────────────────── */
function RateCard({ label, cpm, total, tone }) {
  const tones = {
    red:   "border-red-500/30 bg-red-500/5",
    green: "border-emerald-500/40 bg-emerald-500/10",
    blue:  "border-blue-500/30 bg-blue-500/5",
  };
  const isMedian = label === "Median";
  return (
    <div className={`rounded-lg border ${tones[tone]} p-3 sm:p-4 text-center ${isMedian ? "sm:scale-105 relative z-10" : ""}`}>
      {isMedian && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-widest bg-emerald-500 text-black font-bold px-2 py-0.5 rounded">
          Target
        </div>
      )}
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">{label}</div>
      <div className="text-2xl sm:text-3xl font-black text-white">${total.toLocaleString()}</div>
      <div className="text-[11px] text-white/60 mt-1">${cpm}/mi</div>
    </div>
  );
}

function BenefitCard({ icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-6 hover:border-[#FF5A1F]/40 transition-colors">
      <div className="w-11 h-11 rounded-lg bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center mb-4">
        {icon}
      </div>
      <div className="text-lg font-bold mb-2">{title}</div>
      <div className="text-white/60 text-sm leading-relaxed">{body}</div>
    </div>
  );
}
